import React, { useState, useRef, useEffect } from "react";
import moment from "moment";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import CalendarIcon from "@/images/icons/calendar-day.svg";
import CaretIcon from "@/images/icons/down-icon.svg";
import "./LessonDatePicker.scss";
import { getSchoolYear } from "@/components/Reports/util/validation";
import { useSelector } from "react-redux";
import { selectSchoolYearCalendar } from "@/store/slices/CalendarSlice";
import { AcademicBreakPeriod } from "@/services/query/calendarAPI";
import { selectWeeklyPlanData } from "@/store/slices";

// Helper function to get all dates between start and end dates
const getDatesInRange = (startDateStr: string, endDateStr: string): Date[] => {
  const dates: Date[] = [];

  // Parse YYYY-MM-DD string components
  const [startYear, startMonth, startDay] = startDateStr.split("-").map(Number);
  // Create a Date object for the start date at local midnight
  const startDate = new Date(startYear, startMonth - 1, startDay);

  const [endYear, endMonth, endDay] = endDateStr.split("-").map(Number);
  // Create a Date object for the end date at local midnight
  const endDate = new Date(endYear, endMonth - 1, endDay);

  // Start iterating from a clone of the start date (local midnight)
  let currentDate = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );

  while (currentDate.getTime() <= endDate.getTime()) {
    // Add a clone of the current date (local midnight) to the array
    dates.push(
      new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate()
      )
    );
    // Increment the date by one day (handles month/year rollovers and DST correctly)
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};

interface LessonDatePickerProps {
  date: string;
  disabled?: boolean;
  align?: "left" | "right";
  onDateChange: (date: string) => void;
  lessonDetailPage?: boolean;
}

export const LessonDatePicker: React.FC<LessonDatePickerProps> = ({
  date,
  disabled = false,
  onDateChange,
  lessonDetailPage,
  align = "left",
}) => {
  const dateObject = moment(date).toDate();
  const [isOpen, setIsOpen] = useState(false);
  const [disabledDates, setDisabledDates] = useState<Date[]>([]);
  const isOpenRef = useRef(isOpen);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const schoolYearCalendar = useSelector(selectSchoolYearCalendar);
  const { dateTo } = useSelector(selectWeeklyPlanData)

  // Update ref when isOpen changes
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Handle click outside to close popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        isOpenRef.current
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Use a Set of timestamps to avoid duplicate Date objects and handle comparisons easily
    const allDisabledTimestamps = new Set<number>();

    // 1. Add academic break periods
    if (schoolYearCalendar?.academicBreakPeriods) {
      schoolYearCalendar.academicBreakPeriods.forEach(
        (period: AcademicBreakPeriod) => {
          const periodDates = getDatesInRange(period.startDate ?? "", period.endDate ?? "");
          periodDates.forEach(d => allDisabledTimestamps.add(d.getTime()));
        }
      );
    }

    // 2. Add Saturdays and Sundays up to the maxDate
    const minDateForIteration = moment().startOf("day").toDate(); // Today, local midnight
    const schoolYearEndDate = moment(getSchoolYear().endDate).toDate(); // maxDate, local midnight

    let currentDateForWeekends = new Date(
      minDateForIteration.getFullYear(),
      minDateForIteration.getMonth(),
      minDateForIteration.getDate()
    );

    while (currentDateForWeekends.getTime() <= schoolYearEndDate.getTime()) {
      const dayOfWeek = currentDateForWeekends.getDay(); // 0 for Sunday, 6 for Saturday
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        // Add a clone representing local midnight for the weekend day
        const weekendDay = new Date(
          currentDateForWeekends.getFullYear(),
          currentDateForWeekends.getMonth(),
          currentDateForWeekends.getDate()
        );
        allDisabledTimestamps.add(weekendDay.getTime());
      }
      currentDateForWeekends.setDate(currentDateForWeekends.getDate() + 1);
    }

    // Convert Set of timestamps back to an array of Date objects
    setDisabledDates(Array.from(allDisabledTimestamps).map(ts => new Date(ts)));
  }, [schoolYearCalendar]); // schoolYearCalendar is the primary dependency

  return (
    <div
      className={`lesson-date-picker ${isOpen ? "open" : ""} ${align}`}
      data-testid="lesson-date-picker"
      ref={wrapperRef}>
      <button
        className={`lesson-date-picker-button ${lessonDetailPage ? "lesson-detail-page-button" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        disabled={moment(date)
          .startOf("day")
          .isSameOrBefore(moment().startOf("day")) || disabled}>
        <img src={CalendarIcon} alt="calendar" />
        {moment(date).format("dddd MM/DD")}
        <img src={CaretIcon} alt="caret"/>
      </button>
      <DateRange
        showSelectionPreview={false}
        months={1}
        direction="horizontal"
        moveRangeOnFirstSelection={false}
        showPreview={false}
        showDateDisplay={false}
        disabledDates={disabledDates}
        ranges={[
          {
            startDate: dateObject,
            endDate: dateObject,
            key: "selection",
          },
        ]}
        minDate={moment().startOf("day").toDate()}
        maxDate={moment(dateTo).startOf("day").add(30, 'days').toDate()}
        onChange={ranges => {
          if (ranges.selection) {
            onDateChange(
              moment(ranges.selection.startDate).format("YYYY-MM-DD")
            );
            setIsOpen(false);
          }
        }}
      />
    </div>
  );
};
